import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  MapPin,
  Phone,
  Globe,
  Mail,
  Facebook,
  Instagram,
  Bus,
  Toilet,
  Building2,
  Footprints,
  Sparkles,
  ExternalLink,
  Locate,
  Leaf,
  WheatOff,
  Milk,
  UtensilsCrossed,
  CalendarPlus,
  ShoppingBag,
} from "lucide-react";
import BookTableModal from "@/components/BookTableModal";
import PickupOrderModal from "@/components/PickupOrderModal";
import DirectoryReviewsSection from "@/components/DirectoryReviewsSection";
import DirectoryFollowButton from "@/components/DirectoryFollowButton";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const Section = ({ title, children, testid }) => (
  <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid={testid}>
    <h2 className="font-serif text-lg text-stone-900 mb-3">{title}</h2>
    {children}
  </section>
);

const NearbyRow = ({ icon: Icon, name, distance_m, walking_time_min, sub }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-stone-100 last:border-0">
    <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-stone-600" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[14px] font-medium text-stone-900 truncate">{name}</div>
      {sub && <div className="text-[12px] text-stone-500 truncate">{sub}</div>}
    </div>
    <div className="text-right shrink-0">
      <div className="text-[13px] font-semibold text-stone-700">
        {distance_m < 1000 ? `${distance_m} m` : `${(distance_m / 1000).toFixed(1)} km`}
      </div>
      {walking_time_min !== null && walking_time_min !== undefined && (
        <div className="text-[11px] text-stone-500 flex items-center gap-0.5 justify-end">
          <Footprints className="w-3 h-3" />
          {walking_time_min} min walk
        </div>
      )}
    </div>
  </div>
);

export default function RestaurantDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState(null);
  const [activeMenuIdx, setActiveMenuIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [showOrder, setShowOrder] = useState(false);

  const fetchData = (loc) => {
    setLoading(true);
    const url = new URL(`${API}/api/directory/restaurants/${slug}`, window.location.origin);
    if (loc) {
      url.searchParams.set("user_lat", String(loc.lat));
      url.searchParams.set("user_lng", String(loc.lng));
    }
    fetch(url.toString())
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(null);
    // Also try to fetch menu (404s if none — silent)
    fetch(`${API}/api/directory/restaurants/${slug}/menu`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.menus && d.menus.length > 0) setMenu(d);
      })
      .catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const requestLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const loc = { lat: p.coords.latitude, lng: p.coords.longitude };
        setUserLoc(loc);
        fetchData(loc);
      },
      () => null,
      { enableHighAccuracy: false, timeout: 5000 },
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500">
        Loading…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center text-stone-500">
        <p className="mb-4">Restaurant not found.</p>
        <Link to="/directory" className="text-stone-900 underline">
          Back to directory
        </Link>
      </div>
    );
  }

  const schema = data.aeo_schema && data.aeo_schema.length > 0 ? data.aeo_schema : null;

  // Active template — apply colours via CSS variables on the page wrapper
  const tpl = data.active_template;
  const tplVars = tpl && tpl.stylesheet ? tpl.stylesheet.css_vars : null;
  const wrapperStyle = tplVars
    ? { ...tplVars, backgroundColor: "var(--tpl-bg, #fafaf9)" }
    : null;
  // Merge holiday section into the menu rendering
  const menuActiveTpl = menu && menu.active_template;
  const enrichedMenus = (() => {
    if (!menu || !menu.menus) return menu?.menus || [];
    if (!menuActiveTpl || !menuActiveTpl.holiday_section) return menu.menus;
    const hsec = {
      ...menuActiveTpl.holiday_section,
      is_holiday: true,
      tpl_id: menuActiveTpl.id,
    };
    // Append holiday section to the first menu
    return menu.menus.map((m, i) =>
      i === 0 ? { ...m, sections: [hsec, ...(m.sections || [])] } : m,
    );
  })();

  return (
    <div className="min-h-screen bg-stone-50" style={wrapperStyle || undefined} data-testid="restaurant-detail-page">
      {/* Hero */}
      <div className="relative">
        <div className="aspect-[21/9] sm:aspect-[16/6] w-full overflow-hidden bg-stone-300">
          <img
            src={data.hero_image_url}
            alt={data.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src =
                "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600&q=70";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>

        <div className="absolute top-4 left-4">
          <Link
            to="/directory"
            data-testid="restaurant-back-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-stone-700 text-[13px] font-medium hover:bg-white shadow"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All restaurants
          </Link>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-5 sm:pb-7">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {data.is_featured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500 text-white text-[11px] font-semibold">
                  <Sparkles className="w-3 h-3" /> Featured
                </span>
              )}
              {data.cuisine && (
                <span className="inline-block px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] font-medium border border-white/30">
                  {data.cuisine}
                </span>
              )}
              {data.is_claimed ? (
                <span className="inline-block px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-[11px] font-semibold">
                  Claimed
                </span>
              ) : (
                <span className="inline-block px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] font-medium border border-white/30">
                  Unclaimed
                </span>
              )}
              {tpl && (
                <span
                  data-testid="active-template-badge"
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                    tpl.tier === "diamond"
                      ? "bg-fuchsia-500/95 text-white border-fuchsia-300"
                      : "bg-white/25 backdrop-blur text-white border-white/40"
                  }`}
                  title={`Template: ${tpl.name} (${tpl.tier})`}
                >
                  <Sparkles className="w-3 h-3" />
                  {tpl.tier === "diamond" ? "💎 " : ""}
                  {tpl.name}
                </span>
              )}
            </div>
            <h1
              className="font-serif text-3xl sm:text-5xl text-white tracking-tight leading-tight"
              data-testid="restaurant-name"
            >
              {data.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-white/90 text-[14px]">
              {data.rating && (
                <span className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold">{data.rating.toFixed(1)}</span>
                  <span className="text-white/70">({data.review_count})</span>
                </span>
              )}
              {data.suburb && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {data.address ? `${data.address}, ${data.suburb}` : data.suburb}
                </span>
              )}
              {data.distance_km !== null && data.distance_km !== undefined && (
                <span className="flex items-center gap-1.5 text-emerald-300">
                  <Footprints className="w-4 h-4" />
                  {data.distance_km} km from you
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Action bar — Book / Pre-Order — full width above grid on mobile */}
        <div className="lg:col-span-3 flex flex-wrap gap-2">
          <button
            data-testid="open-book-table"
            onClick={() => setShowBook(true)}
            className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-stone-900 text-white font-semibold hover:bg-stone-800 transition-colors"
          >
            <CalendarPlus className="w-4 h-4" /> Book a table
          </button>
          {menu && menu.menus && menu.menus.length > 0 && (
            <button
              data-testid="open-pickup-order"
              onClick={() => setShowOrder(true)}
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> Pre-order pickup
            </button>
          )}
          {data.phone && (
            <a
              href={`tel:${data.phone}`}
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-stone-200 text-stone-700 font-semibold hover:border-stone-400"
            >
              <Phone className="w-4 h-4" /> Call now
            </a>
          )}
        </div>

        {/* Left column */}
        <div className="lg:col-span-2 space-y-5">
          {(data.ai_site_description || data.business_description || data.ai_summary) && (
            <Section title="About" testid="section-about">
              <div className="prose prose-stone max-w-none text-[15px] leading-relaxed text-stone-700">
                {data.ai_site_description ? (
                  <p>{data.ai_site_description}</p>
                ) : data.business_description ? (
                  <p>{data.business_description}</p>
                ) : (
                  <p>{data.ai_summary}</p>
                )}
              </div>
            </Section>
          )}

          {(data.ai_strengths || data.ai_best_for) && (
            <Section title="What people love" testid="section-highlights">
              {data.ai_strengths && (
                <div className="mb-3">
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
                    Strengths
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.ai_strengths.split(",").map((s, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-medium"
                      >
                        {s.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.ai_best_for && (
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
                    Best for
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.ai_best_for.split(",").map((s, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 text-[12px] font-medium"
                      >
                        {s.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* MENU — Real restaurant menu from MariaDB */}
          {menu && menu.menus && menu.menus.length > 0 && (
            <Section title="Menu" testid="section-menu">
              {menu.menus.length > 1 && (
                <div className="flex gap-2 mb-4 -mx-1 overflow-x-auto pb-1">
                  {menu.menus.map((m, i) => (
                    <button
                      key={m.id}
                      data-testid={`menu-tab-${i}`}
                      onClick={() => setActiveMenuIdx(i)}
                      className={`shrink-0 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-all ${
                        activeMenuIdx === i
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                      }`}
                    >
                      {m.menu_name}
                    </button>
                  ))}
                </div>
              )}

              {(() => {
                const m = enrichedMenus[activeMenuIdx];
                if (!m) return null;
                return (
                  <div className="space-y-5">
                    {m.sections.map((s) => (
                      <div
                        key={(s.name || "") + (s.sort_order || 0) + (s.is_holiday ? "-hol" : "")}
                        className={s.is_holiday ? "-mx-2 px-3 py-3 rounded-xl border" : ""}
                        style={s.is_holiday ? {
                          backgroundColor: "var(--tpl-surface, #fef3c7)",
                          borderColor: "var(--tpl-accent, #f59e0b)",
                          color: "var(--tpl-ink, #1c1917)",
                        } : undefined}
                        data-testid={s.is_holiday ? "holiday-section" : undefined}
                      >
                        <h3
                          className={`font-serif text-[17px] mb-2 border-b pb-1.5 flex items-center justify-between gap-3 ${s.is_holiday ? "" : "text-stone-900 border-stone-200"}`}
                          style={s.is_holiday ? { borderColor: "var(--tpl-line, rgba(0,0,0,0.1))", color: "var(--tpl-ink, #1c1917)" } : undefined}
                        >
                          <span className="flex items-center gap-2">
                            {s.is_holiday && (
                              <span
                                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: "var(--tpl-accent, #f59e0b)", color: "var(--tpl-accent-ink, #fff)" }}
                              >
                                Limited
                              </span>
                            )}
                            {s.name}
                          </span>
                          {s.price_headers && s.price_headers.some((h) => h) && (
                            <span className="text-[11px] font-sans font-normal text-stone-400 uppercase tracking-wider">
                              {s.price_headers.filter((h) => h).join(" · ")}
                            </span>
                          )}
                        </h3>
                        {s.footnote && s.is_holiday && (
                          <p className="text-[12px] italic mb-2" style={{ color: "var(--tpl-ink-soft, #57534e)" }}>{s.footnote}</p>
                        )}
                        <div className="space-y-2.5">
                          {s.items.map((it, idx) => {
                            const dietary = [];
                            if (it.dietary?.gf) dietary.push({ label: "GF", icon: WheatOff });
                            if (it.dietary?.veg) dietary.push({ label: "V", icon: Leaf });
                            if (it.dietary?.vegan) dietary.push({ label: "VG", icon: Leaf });
                            if (it.dietary?.df) dietary.push({ label: "DF", icon: Milk });
                            const priceTxt =
                              it.price ||
                              (it.prices &&
                                (typeof it.prices === "object" && !Array.isArray(it.prices)
                                  ? Object.values(it.prices).filter((p) => p).join("  /  ")
                                  : it.prices.filter((p) => p).join("  /  ")));
                            return (
                              <div
                                key={idx}
                                className="flex items-start gap-3 py-1.5"
                                data-testid={`menu-item-${idx}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-2 flex-wrap">
                                    <span
                                      className={`font-medium text-[14.5px] capitalize ${s.is_holiday ? "" : "text-stone-900"}`}
                                      style={s.is_holiday ? { color: "var(--tpl-ink, #1c1917)" } : undefined}
                                    >
                                      {it.name}
                                    </span>
                                    {dietary.map((d, di) => (
                                      <span
                                        key={di}
                                        className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
                                        title={d.label}
                                      >
                                        {d.label}
                                      </span>
                                    ))}
                                  </div>
                                  {it.description && (
                                    <div
                                      className={`text-[13px] leading-snug mt-0.5 ${s.is_holiday ? "" : "text-stone-500"}`}
                                      style={s.is_holiday ? { color: "var(--tpl-ink-soft, #57534e)" } : undefined}
                                    >
                                      {it.description}
                                    </div>
                                  )}
                                </div>
                                {priceTxt && (
                                  <div
                                    className={`shrink-0 text-[14px] font-semibold tabular-nums ${s.is_holiday ? "" : "text-stone-700"}`}
                                    style={s.is_holiday ? { color: "var(--tpl-ink, #1c1917)" } : undefined}
                                  >
                                    {priceTxt}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {m.footnote && (
                      <p className="text-[12px] text-stone-400 italic pt-3 border-t border-stone-100">
                        {m.footnote}
                      </p>
                    )}
                  </div>
                );
              })()}
            </Section>
          )}

          {/* Nearby — KILLER FEATURE */}
          <Section title="Nearby" testid="section-nearby">
            {!userLoc && (
              <button
                data-testid="enable-location-btn"
                onClick={requestLocation}
                className="w-full mb-3 px-4 py-2.5 rounded-lg bg-stone-900 text-white text-[13px] font-medium flex items-center justify-center gap-2 hover:bg-stone-800"
              >
                <Locate className="w-4 h-4" /> Enable location for walking times from you
              </button>
            )}
            {data.nearby.toilet && (
              <NearbyRow
                icon={Toilet}
                name={data.nearby.toilet.name}
                distance_m={data.nearby.toilet.distance_m}
                walking_time_min={data.nearby.toilet.walking_time_min}
                sub={data.nearby.toilet.accessible ? "Wheelchair accessible" : "Public toilet"}
              />
            )}
            {data.nearby.transport && (
              <NearbyRow
                icon={Bus}
                name={data.nearby.transport.name}
                distance_m={data.nearby.transport.distance_m}
                walking_time_min={data.nearby.transport.walking_time_min}
                sub={`${data.nearby.transport.mode || "Transport"}${
                  data.nearby.transport.routes ? ` · Routes: ${data.nearby.transport.routes}` : ""
                }`}
              />
            )}
            {data.nearby.landmarks &&
              data.nearby.landmarks.map((L, i) => (
                <NearbyRow
                  key={i}
                  icon={Building2}
                  name={L.name}
                  distance_m={L.distance_m}
                  walking_time_min={L.walking_time_min}
                  sub={L.type}
                />
              ))}
            {!data.nearby.toilet &&
              !data.nearby.transport &&
              (!data.nearby.landmarks || data.nearby.landmarks.length === 0) && (
                <p className="text-[14px] text-stone-500">
                  Proximity data coming soon for this venue.
                </p>
              )}
          </Section>

          {/* Reviews — public feed + submit. Sits under Nearby so the page
              flow reads: About → Highlights → Menu → Nearby → Reviews. */}
          <DirectoryReviewsSection slug={slug} bizName={data.name} />
        </div>

        {/* Right column — contact + CTAs */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <div className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
            <h3 className="font-serif text-lg text-stone-900 mb-1">Contact</h3>
            {data.phone && (
              <a
                data-testid="contact-phone"
                href={`tel:${data.phone}`}
                className="flex items-center gap-3 text-[14px] text-stone-700 hover:text-stone-900"
              >
                <Phone className="w-4 h-4 text-stone-400" />
                {data.phone}
              </a>
            )}
            {data.website && (
              <a
                data-testid="contact-website"
                href={data.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[14px] text-stone-700 hover:text-stone-900 break-all"
              >
                <Globe className="w-4 h-4 text-stone-400 shrink-0" />
                <span className="truncate">{data.website.replace(/^https?:\/\//, "")}</span>
                <ExternalLink className="w-3 h-3 ml-auto shrink-0" />
              </a>
            )}
            {data.email && (
              <a
                data-testid="contact-email"
                href={`mailto:${data.email}`}
                className="flex items-center gap-3 text-[14px] text-stone-700 hover:text-stone-900 truncate"
              >
                <Mail className="w-4 h-4 text-stone-400 shrink-0" />
                <span className="truncate">{data.email}</span>
              </a>
            )}
            <div className="flex gap-2 pt-1">
              {data.facebook && (
                <a
                  href={data.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {data.instagram && (
                <a
                  href={data.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {!data.is_claimed && (
            <div
              className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl border border-amber-200 p-5"
              data-testid="claim-cta"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span className="font-semibold text-stone-800">
                  Is this your restaurant?
                </span>
              </div>
              <p className="text-[13px] text-stone-600 mb-3 leading-relaxed">
                Claim this listing on HustlePlate to add your menu, take bookings, run
                loyalty, and get an AI-written description that ranks on Google.
              </p>
              <Link
                to={`/claim-business?slug=${slug}`}
                className="block w-full text-center px-4 py-2.5 rounded-lg bg-stone-900 text-white text-[14px] font-semibold hover:bg-stone-800"
              >
                Claim this listing
              </Link>
            </div>
          )}

          {data.lat && data.lng && (
            <a
              data-testid="directions-btn"
              href={`https://www.google.com/maps/dir/?api=1&destination=${data.lat},${data.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center px-4 py-3 rounded-lg bg-white border border-stone-200 text-stone-700 text-[14px] font-medium hover:border-stone-400"
            >
              <MapPin className="w-4 h-4 inline mr-1.5" />
              Get directions
            </a>
          )}

          <DirectoryFollowButton slug={slug} bizName={data.name} />
        </aside>
      </main>

      {/* SEO: JSON-LD schema injection */}
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schema }}
        />
      )}

      <footer className="max-w-5xl mx-auto px-4 py-10 text-center text-[12px] text-stone-400">
        Powered by HustlePlate ·{" "}
        <Link to="/directory" className="underline">
          Back to directory
        </Link>
      </footer>

      {showBook && (
        <BookTableModal restaurant={data} onClose={() => setShowBook(false)} />
      )}
      {showOrder && menu && (
        <PickupOrderModal
          restaurant={data}
          menu={menu}
          onClose={() => setShowOrder(false)}
        />
      )}
    </div>
  );
}
